import React, { useEffect, useRef, useState } from 'react';
import { CreditCard, Shield, CheckCircle, X, Loader, ExternalLink } from 'lucide-react';

interface StripePaymentProps {
  isOpen: boolean;
  onClose: () => void;
  onPaymentSuccess: () => void;
  onRedirectStart?: () => void;
  petId: number;
  petName: string;
  autoStart?: boolean;
}

function getApiBase() {
  return typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:8000/api/'
    : (import.meta.env.VITE_API_URL);
}

const StripePayment: React.FC<StripePaymentProps> = ({
  isOpen,
  onClose,
  onRedirectStart,
  petId,
  petName,
  autoStart = false,
}) => {
  const [preparing, setPreparing] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const autoStartedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setCheckoutUrl(null);
      autoStartedRef.current = false;
      return;
    }
  }, [isOpen, petId]);

  if (!isOpen) return null;

  const createCheckoutSession = async (): Promise<string> => {
    if (checkoutUrl) return checkoutUrl;
    setError(null);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Please sign in to continue.');
      }

      const apiBase = getApiBase();
      const res = await fetch(`${apiBase}stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ pet_id: petId }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to start checkout');
      }

      if (!data.checkout_url) {
        throw new Error('Checkout URL missing from response');
      }

      setCheckoutUrl(data.checkout_url);
      return data.checkout_url as string;
    } catch (err: any) {
      console.error('Checkout start error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      throw err;
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    if (!autoStart) return;
    if (autoStartedRef.current) return;
    autoStartedRef.current = true;

    // Let the modal paint before preparing the session
    const t = window.setTimeout(() => {
      setPreparing(true);
      createCheckoutSession()
        .catch(() => {
          // error is already set in createCheckoutSession
        })
        .finally(() => setPreparing(false));
    }, 150);

    return () => window.clearTimeout(t);
  }, [isOpen, autoStart]);

  const goToCheckout = async () => {
    setRedirecting(true);
    onRedirectStart?.();
    try {
      const url = await createCheckoutSession();
      window.location.assign(url);
    } catch {
      setRedirecting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div
          className="px-6 py-5 text-white"
          style={{ background: 'var(--pmc-primary)' }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CreditCard className="w-6 h-6" />
              <h2 className="text-lg font-semibold font-poppins">Registration Fee</h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        {(preparing || redirecting) ? (
          <div className="p-10 flex flex-col items-center justify-center gap-3">
            <Loader className="w-7 h-7 animate-spin text-[color:var(--pmc-primary)]" />
            <p className="text-sm text-gray-600 font-inter">
              {redirecting ? 'Redirecting to secure checkout…' : 'Preparing secure checkout…'}
            </p>
            {!!error && (
              <div className="mt-2 w-full p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 font-inter">
                {error}
              </div>
            )}
          </div>
        ) : (
          <div className="p-6 space-y-5">
          <div className="text-center">
            <p className="text-gray-600 text-sm font-inter">
              You’ll be redirected to Stripe to complete payment for
            </p>
            <p className="text-lg font-semibold text-gray-900 font-poppins mt-1">
              {petName}
            </p>
          </div>

          <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-inter">Pet Registration Fee</p>
                <p className="text-xs text-gray-400 font-inter mt-0.5">One-time payment</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900 font-poppins">$25.00</p>
                <p className="text-xs text-gray-400 font-inter">USD</p>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-2 text-xs text-gray-500">
            <Shield className="w-4 h-4 flex-shrink-0 mt-0.5 text-green-500" />
            <p className="font-inter">
              Payments are securely processed by Stripe. Your card details are never stored on our servers.
            </p>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 font-inter">
              {error}
            </div>
          )}

          <div className="space-y-3">
            <button
              type="button"
              onClick={goToCheckout}
              disabled={redirecting || preparing}
              className="w-full py-3 px-4 text-white rounded-full font-medium font-poppins text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ background: 'var(--pmc-primary)' }}
            >
              {redirecting ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Redirecting…</span>
                </>
              ) : preparing ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Preparing checkout…</span>
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4" />
                  <span>Continue to secure checkout</span>
                </>
              )}
            </button>
          </div>
        </div>
        )}

        <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
          <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
            <CheckCircle className="w-3 h-3" />
            <span className="font-inter">Powered by Stripe</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StripePayment;
